import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import GameSetupShell, { LevelStart, RunHeader, TileGrid, useGameStats } from '../../components/Games2/GameSetupShell';

const LEVELS = [
  { id: 1, name: 'Level 1', description: 'Practice (Slow)', feedback: false, color: 'green', totalRounds: 20, details: ['20 rounds', 'Slow rotation speed', 'Pause after initial view', 'No feedback'] },
  { id: 2, name: 'Level 2', description: 'Practice (Mixed)', feedback: true, color: 'yellow', totalRounds: 35, details: ['35 rounds', 'Mixed rotation speeds', 'Pause after initial view', 'Feedback enabled'] },
  { id: 3, name: 'Level 3', description: 'Real Test Mode', feedback: false, color: 'rose', totalRounds: 35, details: ['35 rounds', 'Fast rotation speed', 'No pause', 'No feedback', 'Real exam conditions'] },
];

const score = (r) => parseFloat(r.final_score) || 0;

const ROTSetup2 = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const game = useGameStats('ROT', subjectId);
  const { stats } = game;
  const [level, setLevel] = useState(3);
  const [assist, setAssist] = useState(false);

  // Visual assist is on by default for practice levels and off for the real test
  useEffect(() => { setAssist(level !== 3); }, [level]);

  const start = () => {
    const l = LEVELS.find((x) => x.id === level);
    navigate(`/games/rot/${subjectId}/play`, { state: { level, feedback: l.feedback, totalRounds: l.totalRounds, assist, subject: stats?.subject } });
  };

  return (
    <GameSetupShell
      code="ROT"
      name="Cube Rotation"
      description="Spatial orientation assessment game"
      game={game}
      heroStats={[
        { label: 'Runs', value: stats?.total_runs || 0 },
        { label: 'Best', value: `${stats?.best_score || 0}%` },
        { label: 'Average', value: `${stats?.average_score || 0}%` },
      ]}
      picker={(
        <LevelStart levels={LEVELS} value={level} onChange={setLevel} onStart={start}>
          <button
            type="button"
            role="switch"
            aria-checked={assist}
            onClick={() => setAssist((v) => !v)}
            className={`mt-3 flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${assist
              ? 'border-cyan-500/40 bg-cyan-500/10'
              : 'border-slate-200 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/[0.04]'}`}
          >
            {assist ? <EyeIcon className="h-5 w-5 text-cyan-600 dark:text-cyan-300" /> : <EyeSlashIcon className="h-5 w-5 text-slate-400" />}
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-slate-900 dark:text-white">Visual assist</span>
              <span className="block text-xs text-slate-500 dark:text-gray-400">Shows the starting cube and the commands as pictures</span>
            </span>
            <span className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors ${assist ? 'bg-cyan-500' : 'bg-slate-300 dark:bg-white/15'}`}>
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${assist ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </span>
          </button>
        </LevelStart>
      )}
      runs={stats?.history || []}
      chart={{ series: [{ label: 'Score', color: '#10B981', value: score, main: true }], max: 100, suffix: '%' }}
      renderRun={(run) => (
        <div className="space-y-4">
          <RunHeader run={run} level={run.level || 1} score={`${score(run).toFixed(1)}%`} scoreLabel={`${run.correct_count}/${run.total_rounds} correct`} />
          {(run.rounds_data || []).length > 0 && (
            <TileGrid
              title="Round results"
              items={run.rounds_data.map((r, i) => ({ label: `R${i + 1}`, value: r.is_correct ? '✓' : '✗', sub: `${r.commands?.length || '?'} cmds`, tone: r.is_correct ? 'good' : 'bad' }))}
            />
          )}
        </div>
      )}
    />
  );
};

export default ROTSetup2;
